import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components';

interface LocationState {
  from?: {
    pathname: string;
  };
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginDemo, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    subdomain: '',
    email: '',
    password: '',
    rememberMe: false,
  });

  const [errors, setErrors] = useState<{
    subdomain?: string;
    email?: string;
    password?: string;
    general?: string;
  }>({});

  const [isLoading, setIsLoading] = useState(false);
  const [isDemoLoading, setIsDemoLoading] = useState(false);

  // Get redirect path from location state or default to dashboard
  const from = (location.state as LocationState)?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Validate subdomain
    if (!formData.subdomain.trim()) {
      newErrors.subdomain = 'Stofa er nauðsynleg';
    } else if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
      newErrors.subdomain = 'Stofa má aðeins innihalda litla bókstafi, tölustafi og bandstrik';
    }

    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = 'Netfang er nauðsynlegt';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ógilt netfang';
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = 'Lykilorð er nauðsynlegt';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDemoLogin = async () => {
    setIsDemoLoading(true);
    setErrors({});
    try {
      await loginDemo();
    } catch (error) {
      setErrors({ general: error instanceof Error ? error.message : 'Demo innskráning mistókst' });
    } finally {
      setIsDemoLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await login(formData.email, formData.password, formData.subdomain);
    } catch (error) {
      console.error('Login error:', error);

      // Handle specific error messages
      const errorMessage = error instanceof Error ? error.message : 'Innskráning mistókst';

      if (errorMessage.includes('Office not found') || errorMessage.includes('Stofa fannst ekki')) {
        setErrors({ subdomain: 'Stofa fannst ekki' });
      } else if (errorMessage.includes('Invalid credentials') || errorMessage.includes('Rangt netfang')) {
        setErrors({ general: 'Rangt netfang eða lykilorð' });
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        setErrors({ general: 'Netvilla, vinsamlegast reyndu aftur' });
      } else {
        setErrors({ general: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-primary via-brand-secondary to-purple-700 p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-white/5 rounded-full blur-3xl" />
      </div>

      {/* Login card */}
      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8 space-y-6 animate-fade-in">
          {/* Logo and title */}
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-xl mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('app.name')}
            </h1>
            <p className="text-gray-600">
              {t('app.tagline')}
            </p>
          </div>

          {/* Error message */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 animate-shake">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-red-800">{errors.general}</p>
              </div>
            </div>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Subdomain field */}
            <div>
              <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700 mb-1.5">
                Stofa
              </label>
              <div className="relative">
                <input
                  id="subdomain"
                  type="text"
                  value={formData.subdomain}
                  onChange={(e) => handleInputChange('subdomain', e.target.value.toLowerCase())}
                  placeholder="minstofa"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all ${
                    errors.subdomain ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={isLoading}
                  autoComplete="organization"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-gray-400 text-sm">.dental.myx.is</span>
                </div>
              </div>
              {errors.subdomain && (
                <p className="mt-1.5 text-sm text-red-600">{errors.subdomain}</p>
              )}
            </div>

            {/* Email field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('auth.email')}
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="nafn@example.is"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all ${
                  errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                disabled={isLoading}
                autoComplete="email"
              />
              {errors.email && (
                <p className="mt-1.5 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Password field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('auth.password')}
              </label>
              <input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="••••••••"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all ${
                  errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                disabled={isLoading}
                autoComplete="current-password"
              />
              {errors.password && (
                <p className="mt-1.5 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Remember me and forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                  className="w-4 h-4 text-brand-primary border-gray-300 rounded focus:ring-brand-primary focus:ring-2"
                  disabled={isLoading}
                />
                <span className="ml-2 text-sm text-gray-600">
                  {t('auth.rememberMe')}
                </span>
              </label>

              <button
                type="button"
                className="text-sm text-brand-primary hover:text-brand-secondary font-medium transition-colors"
                disabled={isLoading}
              >
                {t('auth.forgotPassword')}
              </button>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-brand-primary to-brand-secondary text-white py-3 px-6 rounded-lg font-medium hover:shadow-lg hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  <span>Skrái inn...</span>
                </>
              ) : (
                <span>{t('auth.loginButton')}</span>
              )}
            </button>
          </form>

          {/* Demo login */}
          <div className="pt-2">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-white text-gray-500">eða</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleDemoLogin}
              disabled={isLoading || isDemoLoading}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 text-amber-800 font-medium hover:bg-amber-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDemoLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  <span>Hleð demo...</span>
                </>
              ) : (
                <>
                  <span>👁</span>
                  <span>Prófa demo (Madenta)</span>
                </>
              )}
            </button>
            <p className="mt-2 text-center text-xs text-gray-500">
              Skoðunarhamur — engar breytingar vistaðar
            </p>
          </div>

          {/* Footer */}
          <div className="pt-4 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Hluti af{' '}
              <a
                href="https://myhealth.myx.is"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary hover:text-brand-secondary font-medium transition-colors"
              >
                MyHealth
              </a>
              {' '}vistkerfinu
            </p>
          </div>
        </div>

        {/* Powered by 2076 */}
        <div className="text-center mt-6">
          <p className="text-white/80 text-sm">
            Útbúið af{' '}
            <a
              href="https://2076.is"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-white hover:text-white/90 transition-colors"
            >
              2076 ehf
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
